import {of, concat, timer, merge, fromEvent} from "rxjs"
import {catchError, filter, mergeMap, takeWhile, switchMap, delay, takeUntil} from "rxjs/operators"
import {actionFactory, actionTypes} from "@/api/income/IncomeReducer"

const IncomeEpics = {
  uploadIncomeDocument: (epic$, {incomeApi}) => {
    return epic$.pipe(filter(({action}) => action.type === actionTypes.uploadIncomeDocument)).pipe(
      switchMap(({action: {payload}, currState}) => {
        const {token, files} = payload

        // Create an array of upload observables for sequential upload
        const uploadObservables = files.map((file, index) => {
          const newFormData = new FormData()
          newFormData.append("file", file)
          const apiPayload = {token, formData: newFormData}

          return incomeApi.uploadIncomeDocument({payload: apiPayload, apiKey: currState.apiKey}).pipe(
            mergeMap((response) => {
              // Dispatch progress action for each completed file
              return of(actionFactory.uploadIncomeDocumentInProgress({
                completedFiles: index + 1,
                totalFiles: files.length,
                fileName: file.name,
                response
              }))
            }),
            catchError((error) => {
              throw error
            })
          )
        })

        // Use concat to upload files sequentially
        return concat(...uploadObservables).pipe(
          // After all files are uploaded, dispatch completion
          mergeMap((progressAction, index) => {
            if (index === files.length - 1) {
              // Last file completed
              return of(
                progressAction,
                actionFactory.uploadIncomeDocumentCompleted()
              ).pipe(delay(1000))
            }
            return of(progressAction)
          }),
          catchError((error) => {
            return of(actionFactory.uploadIncomeDocumentFailed({error, payload}))
          })
        )
      })
    )
  },

  requestConsent: (epic$, {incomeApi}) => {
    return epic$.pipe(filter(({action}) => action.type === actionTypes.requestConsent)).pipe(
      switchMap(({action: {payload}, currState}) => {
        return incomeApi.requestConsent({payload, apiKey: currState.apiKey}).pipe(
          mergeMap(({data}) => of(actionFactory.requestConsentCompleted(data))),
          catchError((error) => of(actionFactory.requestConsentFailed({error, payload})))
        )
      })
    )
  },

  pollConsentStatus: (epic$, {incomeApi}) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.pollConsentStatus),
      switchMap(({action: {payload}, currState}) => {
        const {token, interval = 3000, expiryDateTime} = payload

        // Calculate real expiry time from API data
        const expiryTime = expiryDateTime ? new Date(expiryDateTime).getTime() : Date.now() + 300000
        const remainingMs = Math.max(0, expiryTime - Date.now())

        // Create cancellation streams
        const stopPolling$ = epic$.pipe(
          filter(({action}) =>
            action.type === actionTypes.stopPollingConsentStatus ||
            action.type === actionTypes.resetConsentState
          )
        )

        // Page visibility handling amd ensure we resume polling when user returns
        const visibilityChange$ = fromEvent(document, 'visibilitychange').pipe(
          filter(() => !document.hidden), // Only when page becomes visible
          switchMap(() => {
            // Check if consent is still valid when user returns
            const timeNow = Date.now()
            if (timeNow < expiryTime) {
              return incomeApi.consentStatus({
                payload: {token},
                apiKey: currState.apiKey
              }).pipe(
                mergeMap(({data}) => of(actionFactory.pollConsentStatusCompleted(data))),
                catchError(error => of(actionFactory.pollConsentStatusFailed({error, payload})))
              )
            } else {
              return of(actionFactory.pollConsentStatusCompleted({status: 'TIMEOUT'}))
            }
          }),
          takeUntil(stopPolling$)
        )

        // Create timeout stream, emits timeout action when consent expires
        const timeout$ = timer(remainingMs).pipe(
          mergeMap(() => of(actionFactory.pollConsentStatusCompleted({
            status: 'TIMEOUT'
          })))
        )

        const polling$ = timer(0, interval).pipe(
          switchMap(() => {
            // Check if still within expiry time before making API call
            if (Date.now() >= expiryTime) {
              return of(actionFactory.pollConsentStatusCompleted({status: 'TIMEOUT'}))
            }
            return incomeApi.consentStatus({
              payload: {token},
              apiKey: currState.apiKey
            }).pipe(
              mergeMap(({data}) => {
                return of(actionFactory.pollConsentStatusCompleted(data))
              }),
              catchError(error =>
                of(actionFactory.pollConsentStatusFailed({error, payload}))
              )
            )
          }),
          // Stop polling when we get a final status
          takeWhile(action => {
            if (action.type === actionTypes.pollConsentStatusCompleted) {
              const status = action.payload?.status
              return !['APPROVED', 'DECLINED', 'FRAUD', 'TIMEOUT', 'ERROR'].includes(status)
            }
            return true // Continue on failed attempts
          }, true), // inclusive - emit the final completing action
          takeUntil(stopPolling$),
          takeUntil(timeout$)
        )
        // Merge polling with visibility change handling
        return merge(polling$, visibilityChange$).pipe(
          takeUntil(timeout$)
        )
      })
    )
  },

  consentedAccounts: (epic$, {incomeApi}) => {
    return epic$.pipe(filter(({action}) => action.type === actionTypes.consentedAccounts)).pipe(
      switchMap(({action: {payload}, currState}) => {
        return incomeApi.consentedAccounts({payload, apiKey: currState.apiKey}).pipe(
          mergeMap(({data}) => of(actionFactory.consentedAccountsCompleted(data))),
          catchError((error) => of(actionFactory.consentedAccountsFailed({error, payload})))
        )
      })
    )
  },

  selectAccount: (epic$, {incomeApi}) => {
    return epic$.pipe(filter(({action}) => action.type === actionTypes.selectAccount)).pipe(
      switchMap(({action: {payload}, currState}) => {
        return incomeApi.selectAccount({payload, apiKey: currState.apiKey}).pipe(
          mergeMap(() => of(actionFactory.selectAccountCompleted())),
          catchError((error) => of(actionFactory.selectAccountFailed({error, payload})))
        )
      })
    )
  },

  autoStartPollingConsent: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.requestConsentCompleted),
      switchMap(({currState}) => {
        const incomeState = currState.incomeState || {}
        if (incomeState.consentToken && incomeState.consentExpiryDateTime) {
          return of(actionFactory.pollConsentStatus({
            token: incomeState.consentToken,
            expiryDateTime: incomeState.consentExpiryDateTime
          }))
        }
        return of()
      })
    )
  },

  autoFetchConsentedAccounts: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.pollConsentStatusCompleted),
      switchMap(({action: {payload}, currState}) => {
        if (payload?.status === "APPROVED") {
          const incomeState = currState.incomeState || {}
          if ( !incomeState.consentedAccountsLoading && !incomeState.consentedAccountsCompleted) {
            return of(actionFactory.consentedAccounts({token: currState.token}))
          }
        }
        return of()
      })
    )
  }
}

export default Object.keys(IncomeEpics).map(k => IncomeEpics[k])